import React from 'react';
import { Box, Typography, Paper, Stack, Alert } from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { FileUploadProps } from './types';
import { useFileUpload } from './useFileUpload';
import FileUploadItem from './FileUploadItem/FileUploadItem';
import './FileUpload.scss';

const FileUpload: React.FC<FileUploadProps> = props => {
  const {
    showFileTypes = true,
    dragDropEnabled = true,
    customStyles,
    placeholder = {},
    multiple = true,
    hideFileList = false,
    allowAllFilesInPicker = false,
    testId,
  } = props;

  const baseTestId = testId || 'file-upload';

  const {
    files,
    isDragOver,
    errors,
    fileInputRef,
    acceptedFormats,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleInputChange,
    handleClickUploadArea,
    removeFile,
    clearAllFiles,
    clearErrors,
    disabled,
  } = useFileUpload(props);

  const formatAcceptedTypes = (): string => {
    return acceptedFormats.map(format => format.toUpperCase()).join(', ');
  };

  return (
    <Box className="fileUpload" style={customStyles?.container}>
      <Paper
        className={`fileUpload__uploadArea ${isDragOver ? 'fileUpload__uploadArea--dragOver' : ''} ${disabled ? 'fileUpload__uploadArea--disabled' : ''}`}
        onDragOver={dragDropEnabled ? handleDragOver : undefined}
        onDragLeave={dragDropEnabled ? handleDragLeave : undefined}
        onDrop={dragDropEnabled ? handleDrop : undefined}
        onClick={handleClickUploadArea}
        style={customStyles?.uploadArea}
        data-testid={`${baseTestId}-area`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={
            allowAllFilesInPicker || acceptedFormats.length === 0
              ? '*'
              : acceptedFormats.join(',')
          }
          multiple={multiple}
          style={{ display: 'none' }}
          onChange={handleInputChange}
          disabled={disabled}
          data-testid={`${baseTestId}-input`}
        />

        <Stack spacing={2} alignItems="center">
          <CloudUploadIcon
            className={`fileUpload__icon ${isDragOver ? 'fileUpload__icon--dragOver' : ''}`}
          />
          <Typography
            variant="h6"
            className="fileUpload__title"
            data-testid={`${baseTestId}-title`}
          >
            {placeholder.title ||
              (isDragOver ? 'Drop files here' : 'Drag & drop files here')}
          </Typography>
          <Typography
            variant="body2"
            className="fileUpload__subtitle"
            data-testid={`${baseTestId}-subtitle`}
          >
            {placeholder.subtitle || 'or click to browse files'}
          </Typography>
          {showFileTypes && (
            <Typography variant="caption" className="fileUpload__formats">
              {placeholder.formats ||
                `Supported formats: ${formatAcceptedTypes()}`}
            </Typography>
          )}
        </Stack>
      </Paper>

      {errors.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {errors.map((error, index) => (
            <Alert
              key={index}
              severity="error"
              onClose={clearErrors}
              sx={{ mb: 1 }}
            >
              {error}
            </Alert>
          ))}
        </Box>
      )}

      {!hideFileList && files.length > 0 && (
        <Box
          className="fileUpload__fileList"
          style={customStyles?.fileList}
          sx={{ mt: 3 }}
        >
          <Stack spacing={2}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle2" color="text.primary">
                Selected Files ({files.length})
              </Typography>
              {multiple && files.length > 1 && (
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{ cursor: 'pointer' }}
                  onClick={clearAllFiles}
                >
                  Clear All
                </Typography>
              )}
            </Box>
            {files.map(file => (
              <FileUploadItem
                key={file.id}
                file={file}
                onDelete={removeFile}
                disabled={disabled}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default FileUpload;
